import {
  artworkRequired,
  isArtworkDelivered,
} from '@printdeal/business-logic';

import { SanitizedLineItem } from '../../../../../../types/order/Order';
import useFileValidation from '../../../../../../hooks/useFileValidation';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

const ReasonsDisabled = {
  ARTWORK_INVALID: 'Artwork invalid',
  ARTWORK_UNAVAILABLE: 'No artwork has been delivered',
  ARTWORK_UNNECESSARY: 'The product does not require artwork',
  LOADING_FILE_VALIDATION: 'The file validation is being loaded',
};

const needsFileValidation = (lineItem: SanitizedLineItem) => artworkRequired(lineItem)
  && isArtworkDelivered(lineItem);

const useDownloadFiles = (lineItem: SanitizedLineItem) => {
  const fileValidation = useFileValidation(lineItem, needsFileValidation(lineItem));

  if (!artworkRequired(lineItem)) {
    // Some products cannot be printed on.
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.ARTWORK_UNNECESSARY,
    };
  }

  if (!isArtworkDelivered(lineItem)) {
    // No point in editing files if there are no files to begin with.
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.ARTWORK_UNAVAILABLE,
    };
  }

  if (fileValidation.isLoading) {
    // While the tools are being fetched, display an appropriate visual.
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.LOADING_FILE_VALIDATION,
    };
  }

  if (fileValidation.isError
    || fileValidation?.data[lineItem.id]?.files.length === 0
    || !fileValidation?.data[lineItem.id]?.valid) {
    dispatchAccountEvent(
      'order_detail_api_failure',
      'validate_files_of_line_item',
      { lineItemId: lineItem.id, actionTriggeringValidation: 'downloadFiles' },
    );

    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.ARTWORK_INVALID,
    };
  }

  if (fileValidation?.data[lineItem.id]?.files.length === 0
    || !fileValidation?.data[lineItem.id]?.valid) {
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.ARTWORK_INVALID,
      fileValidationData: fileValidation.data[lineItem.id],
    };
  }

  return {
    enabled: true,
    fileValidationData: fileValidation.data[lineItem.id],
  };
};

export default useDownloadFiles;
